import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateProject, fetchProjectById } from '../controllers/projectsController';

const statusOptions = [
  { value: 0, label: 'Planlandı' },
  { value: 1, label: 'Devam Ediyor' },
  { value: 2, label: 'Tamamlandı' },
  { value: 3, label: 'Beklemede' },
  { value: 4, label: 'İptal' },
];

const EditProjectDialog = ({ open, onClose, project, onSuccess }) => {
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    theme: '',
    purpose: '',
    logoCariCode: '',
    status: 0,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project && open) {
      setEditForm({
        name: project.name || '',
        description: project.description || '',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        theme: project.theme || '',
        purpose: project.purpose || '',
        logoCariCode: project.logoCariCode || '',
        status: project.status ?? 0,
        isActive: project.isActive ?? true,
      });
    }
  }, [project, open]);

  const canSave =
    editForm.name.trim().length > 0 && editForm.logoCariCode.trim().length > 0;

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!project?.id || !canSave) return;
    setSaving(true);
    try {
      const payload = {
        id: project.id,
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        groupMembers: (project.groupMembers || []).map((m) => ({
          id: m.id,
          fullName: m.fullName,
          profilePictureUrl: m.profilePictureUrl || null,
          role: m.role || null,
          isLeader: m.isLeader || false,
          memberType: m.memberType ?? 0,
          parentMemberId: m.parentMemberId || null,
        })),
        startDate: editForm.startDate || null,
        endDate: editForm.endDate || null,
        theme: editForm.theme.trim() || null,
        purpose: editForm.purpose.trim() || null,
        logoCariCode: editForm.logoCariCode.trim(),
        coverImageUrl: project.coverImageUrl || null,
        status: editForm.status,
        order: project.order ?? 0,
        isActive: editForm.isActive,
      };
      await updateProject(payload);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (e) {
      alert(e?.message || 'Proje güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Projeyi Düzenle
        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Proje Adı
              </Typography>
              <TextField
                fullWidth
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                placeholder=""
                disabled={saving}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Açıklama
              </Typography>
              <TextField
                fullWidth
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder=""
                multiline
                minRows={2}
                disabled={saving}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Başlangıç Tarihi
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Bitiş Tarihi
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editForm.endDate}
                  onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Tema
                </Typography>
                <TextField
                  fullWidth
                  value={editForm.theme}
                  onChange={(e) => setEditForm({ ...editForm, theme: e.target.value })}
                  placeholder=""
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Amaç
                </Typography>
                <TextField
                  fullWidth
                  value={editForm.purpose}
                  onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })}
                  placeholder=""
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
            </Stack>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Logo cari kod
              </Typography>
              <TextField
                fullWidth
                required
                value={editForm.logoCariCode}
                onChange={(e) => setEditForm({ ...editForm, logoCariCode: e.target.value })}
                placeholder=""
                disabled={saving}
                inputProps={{ maxLength: 60 }}
                helperText="Proje bağışı entegrasyonu için Logo ERP cari kodu (zorunlu)."
                FormHelperTextProps={{ sx: { mt: 0.75, fontSize: '12px' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Durum
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: Number(e.target.value) })}
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Durum
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editForm.isActive}
                        onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                        disabled={saving}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#0E4D94',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#0E4D94',
                          },
                        }}
                      />
                    }
                    label={editForm.isActive ? 'Aktif' : 'Pasif'}
                    sx={{
                      ml: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '13px',
                        ml: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !canSave}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0a3d7a',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditProjectDialog;
